import React, { CSSProperties, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select } from 'antd';

import { syncProjects } from '../../projects/actions';
import { Project } from '../../../models/Project';
import {
  RootReducerState,
  getOffice,
  getProjectsIsFetching,
  getUserMe,
  getInternalOfficesByIdState,
  getInternalOfficeIds,
} from '../../../apps/main/rootReducer';
import { distinctByProperty, isTemporaryId } from '../../../util';
import { useTranslation } from 'react-i18next';
import {
  FetchProjectContextType,
  OfficeId,
  OfficeRole,
  ProjectId,
} from '../../../models/Types';
import { Office } from '../../../models/Office';
import { createSelector } from 'reselect';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sortProjects } from '../utils';
import useProjectsContext from '../hooks/useProjectsContext';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  disabledPicker: {
    backgroundColor: 'rgb(0,0,0,0.05)',
  },
  icon: {
    marginRight: theme.old.spacing.unit(1.5),
  },
}));

export const projectSelector = (
  myProjects: Project[],
  allProjects: Project[],
  contextType: FetchProjectContextType,
  officeId?: OfficeId,
  officeRoles?: OfficeRole[],
  onlyOfficeProjects?: boolean
) =>
  createSelector<
    [
      (state: RootReducerState) => { [id: OfficeId]: Office },
      (state: RootReducerState) => OfficeId[],
      (state: RootReducerState) => Office,
      (state: RootReducerState) => {
        [key: string]: OfficeRole[];
      },
    ],
    Project[]
  >(
    getInternalOfficesByIdState,
    getInternalOfficeIds,
    (state) => getOffice(state, officeId),
    (state) => getUserMe(state)?.prioData?.officeRoles,
    (officeById, officeIds, office, officeRolesByOfficeId) => {
      const projectsMe = myProjects;
      const projectsAll = allProjects;
      if (contextType === 'office') {
        if (officeId) {
          return projectsAll.filter(
            (project) => office?.companyId === project?.subsidiaryId
          );
        }
        const offices = officeIds
          .map((id) => officeById[id])
          .filter((office) => !!office);
        const officeProjects = projectsAll.filter((project) =>
          offices.some(
            (office) =>
              (officeRoles ?? []).find((role) =>
                officeRolesByOfficeId[office.officeId]?.includes(role)
              ) && office?.companyId === project?.subsidiaryId
          )
        );
        if (onlyOfficeProjects) {
          return officeProjects;
        }
        return distinctByProperty(
          [...officeProjects, ...projectsMe],
          'projectId'
        );
      } else {
        return contextType === 'me' ? projectsMe : projectsAll;
      }
    }
  );

declare type Placement = 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';

interface ProjectPickerProps {
  value?: string | string[];
  label?: string;
  onChange?: (value: string | string[]) => void;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  filter?: (project: Project) => boolean;
  className?: string;
  fetch?: boolean;
  maxTagCount?: number;
  contextType?: FetchProjectContextType;
  officeId?: OfficeId;
  officeRoles?: OfficeRole[];
  onlyOfficeProjects?: boolean;
  onlyProjectIds?: ProjectId[];
  bordered?: boolean;
  dropdownStyle?: CSSProperties;
  placement?: Placement;
  dropdownMatchSelectWidth?: boolean | number;
  includeArchived?: boolean;
  include?: Project | Project[];
  withDefaultProjectId?: boolean;
}

export const ProjectPicker: React.FC<ProjectPickerProps> = (props) => {
  const classes = useStyles();
  const {
    className,
    value,
    label,
    onChange,
    disabled,
    multiple,
    fetch,
    filter,
    maxTagCount,
    contextType,
    officeId,
    officeRoles,
    onlyOfficeProjects,
    onlyProjectIds,
    bordered,
    dropdownStyle,
    placement,
    dropdownMatchSelectWidth,
    includeArchived = true,
    include,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [focused, setFocused] = useState(false);

  React.useEffect(() => {
    if (fetch && contextType !== 'me') {
      dispatch(syncProjects());
    }
  }, [dispatch, fetch, contextType]);

  const { myProjects, allProjects } = useProjectsContext();

  const projectsFromSelector: Project[] = useSelector(
    projectSelector(
      myProjects,
      allProjects,
      contextType ?? 'me',
      officeId,
      officeRoles,
      onlyOfficeProjects
    )
  );

  const handleOnChange: (value: string) => void = onChange
    ? (value: string) => {
        onChange(value ?? '');
      }
    : null;

  const activeProjects = useMemo(() => {
    return sortProjects(projectsFromSelector).filter(
      (project) =>
        !isTemporaryId(project.projectId) && (!filter || filter(project))
    );
  }, [projectsFromSelector, filter]);

  const projects = useMemo(() => {
    let allProjects = onlyProjectIds
      ? activeProjects.filter((project) =>
          onlyProjectIds.includes(project.projectId)
        )
      : activeProjects;

    if (include) {
      const includeProjectsArray = Array.isArray(include) ? include : [include];
      includeProjectsArray.forEach((includeProject) => {
        if (
          !allProjects.find(
            (project) => project.projectId === includeProject.projectId
          )
        ) {
          allProjects.push(includeProject);
        }
      });
    }

    return allProjects;
  }, [activeProjects, onlyProjectIds, include]);

  const isFetching = useSelector(getProjectsIsFetching);

  return (
    <Select
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(omitted) =>
        t('common:select.omittedPlaceholder', { count: omitted.length })
      }
      className={classNames(classes.root, className, {
        [classes.disabledPicker]: disabled || isFetching,
      })}
      mode={multiple ? 'multiple' : null}
      showSearch
      value={value}
      placeholder={label}
      optionFilterProp="label"
      onChange={handleOnChange}
      filterOption={(input, option) =>
        option.title.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      disabled={disabled || isFetching}
      loading={isFetching}
      bordered={bordered}
      dropdownStyle={dropdownStyle}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      suffixIcon={
        <FontAwesomeIcon
          icon={['fal', focused ? 'search' : 'chevron-down']}
          style={{ color: '#0052cc', width: '10px' }}
        />
      }
      placement={placement}
      options={(projects ?? [])
        .filter((project) => includeArchived || !project.isArchived)
        .map((project: Project) => ({
          value: project.projectId,
          label: (
            <>
              {project.parentProject && (
                <FontAwesomeIcon
                  icon={['fal', 'horizontal-rule']}
                  size="sm"
                  className={classes.icon}
                />
              )}
              {`${project.number} ${project.shortName ?? project.name}`}
            </>
          ),
          title: `${project.number} ${project.shortName ?? project.name}`,
        }))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

export default ProjectPicker;
